import React, { useEffect, useState, useContext, useMemo } from "react";
import henceforthApi from "../utils/henceforthApi";
import { GlobalContext } from "../context/Provider";
import { Link, useLocation, useMatch, useNavigate } from "react-router-dom";
import { ColDef } from "ag-grid-community";
import { Edit2, MoreHorizontal, Plus, Search } from "lucide-react";

import PaginationLayout from "../components/PaginationLayout";
import AgGridTable from "../components/ui/AgGridTable";
import { Input } from "../components/ui/input";
import { Button } from "../components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "../components/ui/dropdown-menu";

interface faq {
  question: string;
  _id: string;
  answer: string;
}

export default () => {
  const navigate = useNavigate();
  const location = useLocation();
  const match: any = useMatch("/faqs/:page");
  const newParam = new URLSearchParams(location.search);
  const limit = 10;
  const { authState, handlePagination, handleFilter, handleError } = useContext(GlobalContext);

  const [totalCount, setTotalCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const [state, setState] = useState({
    data: [] as Array<faq>,
    total_count: 0,
  });

  if (authState.access_token) {
    henceforthApi.setToken(authState.access_token);
  }

  const currentPage = match?.params?.page || "1";

  useEffect(() => {
    const init = async () => {
      setLoading(true);
      try {
        let apiRes = await henceforthApi.Faq.pagination(
          currentPage,
          newParam.toString(),
          limit
        );
        setState({
          ...state,
          ...apiRes.data,
        });
        setTotalCount(apiRes.data.total_count || 0);
      } catch (error: any) {
        setState({ data: [], total_count: 0 });
        setTotalCount(0);
        handleError(error);
      } finally {
        setLoading(false);
      }
    };
    init();
  }, [currentPage, newParam.get("search")]);

  const stripHtml = (html: string) => {
    const tmp = document.createElement("DIV");
    tmp.innerHTML = html;
    return tmp.textContent || tmp.innerText || "";
  };

  const columnDefs = useMemo<ColDef<faq>[]>(() => {
    const pageNum = Number(currentPage) || 1;
    return [
      {
        headerName: "Sr No.",
        field: "_id",
        width: 75,
        minWidth: 70,
        maxWidth: 85,
        suppressSizeToFit: true,
        valueGetter: (p) => {
          if (!p.node || p.node.rowIndex === null || p.node.rowIndex === undefined) return "";
          return (pageNum - 1) * limit + (p.node.rowIndex + 1);
        },
      },
      {
        headerName: "Question",
        field: "question",
        minWidth: 260,
        flex: 1.5,
        cellRenderer: (p: any) => {
          return (
            <span className="font-medium text-slate-800 text-sm">
              {p.value || "Untitled Question"}
            </span>
          );
        },
      },
      {
        headerName: "Answer",
        field: "answer",
        minWidth: 320,
        flex: 2.5,
        cellRenderer: (p: any) => {
          const raw = stripHtml(p.value || "");
          return (
            <span className="text-slate-600 text-sm truncate block" title={raw}>
              {raw || "No answer provided"}
            </span>
          );
        },
      },
      {
        headerName: "Actions",
        width: 90,
        minWidth: 80,
        maxWidth: 100,
        pinned: "right",
        cellRenderer: (p: any) => {
          const row = p.data;
          const faqId = row?._id;

          return (
            <div className="flex items-center justify-center h-full">
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <button
                    type="button"
                    className="table-action-dots p-1 text-slate-500 hover:text-slate-800 focus:outline-none"
                    aria-label="Actions"
                  >
                    <MoreHorizontal className="w-5 h-5" />
                  </button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-36">
                  <DropdownMenuItem
                    onClick={() => navigate(`/faq/edit/${faqId}`)}
                    className="gap-2 cursor-pointer text-slate-700"
                  >
                    <Edit2 className="w-4 h-4 text-slate-500" />
                    <span>Edit FAQ</span>
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </div>
          );
        },
      },
    ];
  }, [currentPage]);

  return (
    <>
      {/* Compact Single Toolbar Row */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3 mb-4">
        {/* Left: Search Input */}
        <div className="relative w-64">
          <Search className="w-4 h-4 absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none" />
          <Input
            placeholder="Search by question..."
            value={newParam.get("search") || ""}
            onChange={(e) => handleFilter("search", e.target.value)}
            className="search-icon-input h-9 text-sm bg-white border-slate-200 focus:bg-white shadow-2xs"
          />
        </div>

        {/* Right: Add FAQ Button */}
        <div className="flex items-center gap-2 flex-wrap">
          <Link to="/faq/add">
            <Button
              variant="default"
              size="sm"
              className="h-9 px-4 text-sm gap-1.5 shadow-xs"
            >
              <Plus className="w-4 h-4" />
              <span>Add FAQ</span>
            </Button>
          </Link>
        </div>
      </div>

      {/* AG Grid Table */}
      <AgGridTable
        rowData={state.data}
        columnDefs={columnDefs}
        loading={loading}
        domLayout="autoHeight"
        emptyMessage="No FAQs found"
      />

      <div className="mt-3">
        <PaginationLayout
          count={totalCount}
          data={state.data}
          page={Number(currentPage)}
          limit={limit}
          loading={loading}
          onPageChange={(val) => handlePagination(val)}
        />
      </div>
    </>
  );
};
